import React, { useState, useEffect } from "react";
import { TextField, makeStyles } from "@material-ui/core";
import Autocomplete, {
    createFilterOptions
} from "@material-ui/lab/Autocomplete";

const useStyles = makeStyles(() => ({
    inputRoot: {
        borderRadius: 10,
        backgroundColor: "#fff",
        paddingRight: "0 !important",
        "& .MuiOutlinedInput-notchedOutline": {
            borderColor: "#d0d5dd"
        },
        "&:hover .MuiOutlinedInput-notchedOutline": {
            borderColor: "#b0b7c3"
        },
        "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
            borderColor: "#2563eb",
            borderWidth: 1.5
        }
    },
    input: {
        padding: '0.24rem !important',
        height: '100%', // Keeps the text vertically centered if the wrapper height changes
        boxSizing: 'border-box'
    },
    label: {
        fontSize: 14,
        marginBottom: 6,
        color: "#344054",
        fontWeight: 500
    }
}));

const filter = createFilterOptions();

const CreatableAutocomplete = ({
    selectedValue,
    setSelectedValue,
    options = [],
    setOptions,
    placeholder,
    disabled = false,
    showRaisedPlaceholder = false,
    disableSelectedOption = false,
    className = "",
    containerClass = ""
}) => {
    const classes = useStyles();

    const [inputValue, setInputValue] = useState("");

    useEffect(() => {
        if (selectedValue) {
            setInputValue(typeof selectedValue === 'string' ? selectedValue : selectedValue.label);
        } else {
            setInputValue("");
        }
    }, [selectedValue]);

    return (
        <div style={{ minWidth: '9rem' }} className={containerClass}>
            {showRaisedPlaceholder && <div className={classes.label}>{placeholder}</div>}

            <Autocomplete
                // ... (keep all your existing Autocomplete props like value, options, onChange, etc.)
                value={selectedValue}
                inputValue={inputValue}
                disabled={disabled}
                options={options}
                freeSolo
                onInputChange={(event, newInputValue) => setInputValue(newInputValue)}
                onBlur={() => {
                    if (selectedValue) {
                        setInputValue(typeof selectedValue === 'string' ? selectedValue : selectedValue.label);
                    } else {
                        setInputValue("");
                    }
                }}
                filterOptions={(options, params) => {
                    // ... keep your existing filter logic
                    const filtered = filter(options, params);
                    const inputValue = params.inputValue;
                    const isExisting = options.some(option => option.label.toLowerCase() === inputValue.toLowerCase());
                    if (inputValue !== "" && !isExisting) {
                        filtered.push({ label: inputValue, value: inputValue, newlyAdded: true });
                    }
                    return filtered;
                }}
                getOptionLabel={(option) => {
                    if (typeof option === "string") {
                        return option;
                    }
                    return option?.label || "";
                }}
                renderOption={(option) => {
                    return (
                        <div>
                            {option.newlyAdded ? `Add "${option.label}"` : option.label}
                        </div>
                    );
                }}
                onChange={(event, newValue) => {
                    if (typeof newValue === "string") {
                        const existingOption = options.find((opt) => opt.label.toLowerCase() === newValue.toLowerCase());

                        if (existingOption) {
                            if (selectedValue?.value === existingOption.value) {
                                return;
                            }

                            if (disableSelectedOption && existingOption.selected) {
                                return;
                            }

                            if (disableSelectedOption) {
                                const tempOptions = options.map(f => ({
                                    ...f,
                                    selected: f.value === existingOption.value ? true : (selectedValue?.value === f.value ? false : f.selected)
                                }));
                                setOptions(tempOptions);
                            }
                            setSelectedValue(existingOption);
                        } else {
                            const newOption = {
                                label: newValue,
                                value: newValue,
                                selected: true,
                            };

                            let tempOptions = JSON.parse(JSON.stringify(options));
                            if (selectedValue?.value) {
                                tempOptions = tempOptions?.map(to => {
                                    if (to?.value === selectedValue?.value) {
                                        return {
                                            ...to,
                                            selected: false,
                                        }
                                    }
                                    return to;
                                })
                            }
                            setOptions([...tempOptions, newOption]);
                            setSelectedValue(newOption);
                        }
                    } else if (newValue && newValue?.newlyAdded) {
                        const newOption = {
                            label: newValue.value,
                            value: newValue.value,
                            selected: true,
                        };
                        let tempOptions = JSON.parse(JSON.stringify(options));
                        if (selectedValue?.value) {
                            tempOptions = tempOptions?.map(to => {
                                if (to?.value === selectedValue?.value) {
                                    return {
                                        ...to,
                                        selected: false,
                                    }
                                }
                                return to;
                            })
                        }

                        setOptions([...tempOptions, newOption]);
                        setSelectedValue(newOption);
                    } else {
                        if (disableSelectedOption) {
                            const tempOptions = options?.map(f => {
                                return {
                                    ...f,
                                    selected: selectedValue?.value === f?.value ? false
                                        : newValue?.value === f?.value ? true
                                            : f?.selected
                                }
                            });

                            setOptions(tempOptions)
                        }
                        setSelectedValue(newValue);
                    }
                }}
                renderInput={(params) => {
                    return (
                        <TextField
                            {...params}
                            placeholder={placeholder}
                            variant="outlined"
                            InputProps={{
                                ...params.InputProps,
                                classes: {
                                    // 2. Append the passed className to the default inputRoot class
                                    root: `${classes.inputRoot} ${className}`.trim(),
                                    input: classes.input
                                }
                            }}
                        />
                    )
                }}
                getOptionDisabled={(option) => disableSelectedOption && option.selected === true}
            />
        </div>
    );
};

export default CreatableAutocomplete;